import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import { membershipToFirestore } from "../models"; // Barrel export
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "./collections";

/**
 * MembershipService
 * Centraliza as operações de banco de dados para o módulo de Memberships (Planos).
 * Coleção: tenants/{idTenant}/memberships
 */
const MembershipService = {
    /**
     * Busca todos as memberships (planos) de um tenant.
     */
    getMemberships: async (idTenant) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBERSHIPS),
                orderBy("order", "asc")
            );
            const querySnapshot = await getDocs(q);
            const memberships = [];
            querySnapshot.forEach((docSnap) => {
                memberships.push({ id: docSnap.id, ...docSnap.data() });
            });
            return memberships;
        } catch (error) {
            console.error("Erro ao listar memberships (planos):", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza uma membership.
     */
    save: async (formData, idTenant, authUser, existingMemberships = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const membershipData = membershipToFirestore(formData, idTenant, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const membershipRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERSHIPS, formData.id);
                const oldMembership = existingMemberships.find(m => m.id === formData.id) || {};
                await updateDoc(membershipRef, membershipData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldMembership, membershipData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.MEMBERSHIPS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: membershipData.name,
                    description: `Atualizou a membership "${membershipData.name}"`,
                    details: changes
                });

                return { id: formData.id, ...membershipData };
            } else {
                // CREATE — define order como último
                if (!formData.order && existingMemberships.length > 0) {
                    membershipData.order = existingMemberships.length;
                }

                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.MEMBERSHIPS), membershipData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.MEMBERSHIPS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: membershipData.name,
                    description: `Criou a membership "${membershipData.name}"`
                });

                return { id: newDocRef.id, ...membershipData };
            }
        } catch (error) {
            console.error("Erro no MembershipService.save:", error);
            throw error;
        }
    },

    /**
     * Remove uma membership.
     */
    delete: async (membershipId, idTenant, authUser, membershipName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.MEMBERSHIPS, membershipId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERSHIPS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: membershipId,
                entityName: membershipName,
                description: `Excluiu a membership "${membershipName || membershipId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no MembershipService.delete:", error);
            throw error;
        }
    },

    /**
     * Reordena a lista de memberships em massa.
     */
    reorder: async (items, idTenant, authUser) => {
        if (!idTenant || !items.length) return;

        try {
            const batch = writeBatch(db);
            items.forEach((item, index) => {
                const docRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERSHIPS, item.id);
                batch.update(docRef, { order: index });
            });

            await batch.commit();

            // Auditoria simplificada do lote
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERSHIPS,
                action: AUDIT_ACTIONS.UPDATE,
                description: `Reordenou a lista de memberships (${items.length} itens)`
            });

            return true;
        } catch (error) {
            console.error("Erro no MembershipService.reorder:", error);
            throw error;
        }
    }
};

export default MembershipService;
